import type { CourseUpdate, CourseWithoutBlocks } from '@/types/course'
import { toNumber } from './coursePatch'

export type SalesModel = 'subscription' | 'standalone' | 'free'

// Минимальный срез курса, нужный для вывода режима продажи — тот же, каким его
// читает боевой плеер (academy-frontend useCourseAccess.ts): бесплатность — это
// цена 0, подписка — isAvailableBySubscription.
export type SalesModelCourse = Pick<
  CourseWithoutBlocks,
  'isAvailableBySubscription' | 'priceRub' | 'usdPrice'
>

// purchaseMode бэк не хранит (PUT /gamified-courses/{id} его не принимает) — режим
// целиком выводится из двух реальных полей. Подписка выставлена намеренно, поэтому
// она побеждает даже при нулевой цене — курс можно продавать по подписке бесплатно
// для владельца подписки, но это не то же самое, что "бесплатный курс".
export const readSalesModel = (course: SalesModelCourse): SalesModel => {
  if (course.isAvailableBySubscription) return 'subscription'
  const isFree = toNumber(course.priceRub) === 0 && toNumber(course.usdPrice) === 0
  return isFree ? 'free' : 'standalone'
}

// Выбор автора в пикере поверх выведенного из курса режима: `from` — то, что показывал
// курс в момент выбора.
export interface SalesModelOverride {
  from: SalesModel
  picked: SalesModel
}

// Что реально показывать пикеру. Выведенный режим — основа (курс единственный источник
// правды и он обновляется после каждого сохранения), выбор автора живёт поверх него, пока
// курс этому выбору не противоречит.
export const resolveSalesModel = (
  derived: SalesModel,
  override: SalesModelOverride | null,
): SalesModel => {
  if (!override) return derived
  const { from, picked } = override
  const stillValid =
    // курс ещё не догнал PUT (рефетч всего курса идёт асинхронно)
    derived === from ||
    // курс подтвердил выбор
    derived === picked ||
    // нулевая цена без подписки — это ОДНОВРЕМЕННО и «Бесплатный», и «Отдельно» без цены:
    // в данных они неразличимы, различает их только выбор автора (о чём его и предупреждает
    // подпись под ценой). Иначе сохранение нулевой цены выкидывало бы автора из «Отдельно».
    (picked === 'standalone' && derived === 'free')
  return stillValid ? picked : derived
}

// Возвращаемый CourseUpdate сужен до реально затрагиваемых полей — иначе TS берёт
// полный интерфейс (с optional mascots/categoryIds) и `{ ...local, ...patch }` в
// PricingSection перестаёт совпадать с CourseWithoutBlocks (там mascots обязателен).
export type SalesModelPatch = Pick<
  CourseUpdate,
  'isAvailableBySubscription' | 'priceRub' | 'usdPrice'
>

// Что писать при выборе режима в UI. standalone намеренно не трогает уже введённые
// цены — сброс их до нуля стёр бы то, что автор набрал перед переключением режима.
// subscription стирает цену явным null (бэк: null стирает, отсутствие ключа не трогает);
// 0 тут НЕЛЬЗЯ — он значит «бесплатно для всех» и открыл бы курс мимо подписки.
export const salesModelPatch = (next: SalesModel): SalesModelPatch => {
  switch (next) {
    case 'subscription':
      return { isAvailableBySubscription: true, priceRub: null, usdPrice: null }
    case 'free':
      return { isAvailableBySubscription: false, priceRub: 0, usdPrice: 0 }
    case 'standalone':
      return { isAvailableBySubscription: false }
  }
}
